import { useState, useRef } from 'react';

const CATEGORIES = [
  { value: '', label: 'Select category...' },
  { value: 'action_figures', label: 'Action Figures' },
  { value: 'arts_crafts', label: 'Arts & Crafts' },
  { value: 'board_games', label: 'Board Games' },
  { value: 'card_games', label: 'Card Games' },
  { value: 'construction', label: 'Construction' },
  { value: 'dolls', label: 'Dolls' },
  { value: 'educational', label: 'Educational' },
  { value: 'electronic', label: 'Electronic' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'plush', label: 'Plush' },
  { value: 'pretend_play', label: 'Pretend Play' },
  { value: 'puzzles', label: 'Puzzles' },
  { value: 'ride_on', label: 'Ride-On' },
  { value: 'sensory', label: 'Sensory' },
  { value: 'stem', label: 'STEM' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'other', label: 'Other' },
];

export default function NewIdeaModal({ onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave({ title, description, category: category || null }, files);
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleFileChange = (e) => {
    setFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-vault-navy">New Idea</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-vault-error text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your idea a name"
                className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your idea in detail. The more you write, the better AI can profile and match it."
                rows={5}
                className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">AI will analyze this to extract category, age range, and cost tier</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold focus:border-transparent bg-white"
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Optional. AI will also suggest a category from your description.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Files</label>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-vault-border rounded-lg p-6 text-center cursor-pointer hover:border-vault-gold/50 transition-colors"
              >
                <span className="text-gray-400 text-sm">Click to upload images, PDFs, or documents</span>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-vault-surface rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-600 truncate">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="text-gray-400 hover:text-vault-error ml-2"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 border border-vault-border text-gray-600 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2.5 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Idea'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
